import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { IconTypeNames } from "Components/Icon";
import { Routes, PRODUCTTYPES, FUND_SOURCE_TYPE } from "Utils/Constants";
import "./TransferFunds.scss";
import useSession from "Utils/Hooks/useSession";
import useTransactionInitiated from "Utils/Hooks/useTransactionInitiated";
import AvailableFundsBase from "Components/AvailableFunds/AvailableFundsBase";
import useFundType from "Utils/Hooks/useFundType";
import CancelButton from "Components/CancelButton";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import settings from "Assets/Icons/settings.svg";
import FormField from "Components/Fields/FormField";
import { AddTrailingZeros } from "Utils/Utils";
import useView, { VIEW } from "Utils/Hooks/useView";
import errorIcon from "Assets/Icons/warning-2.svg";
import { faro } from "@grafana/faro-react";
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
} from "@pav/pav-products-design-system";
import { Controller, useForm } from "react-hook-form";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";

const TransferFunds = ({
  withdraw,
  vipApi,
  limitAmount,
  minAmount,
  messages,
  fundingSources,
  defaultFund,
  fundTypeName,
  isPlaid,
  operatorDailyLimit,
  fundingSourceList,
  preferredCurrentLimit,
  preferredPreviousLimit,
  noWarranty,
  unVerifiedBank,
}) => {
  const { session, fetchSession } = useSession();
  const view = useView();
  const productType =
    session?.patronProductType === PRODUCTTYPES.PREFERRED
      ? "VIP Preferred"
      : "VIP Online";
  const isVIPPreferred = session?.patronProductType === PRODUCTTYPES.PREFERRED;
  const pageTitle =
    (isVIPPreferred ? "VIPP" : "VIPO") +
    " - " +
    (withdraw ? "Withdraw" : "Deposit");
  document.title = pageTitle;
  const [showTerms, setShowTerms] = useSessionStorage("vip-showTerms");
  const [sessionStorageTransAmount, setSessionStorageTransAmount] =
    useSessionStorage("vip-transaction-amount");
  const [isRepeat, setIsRepeat] = useSessionStorage("isRepeat", false);
  const { setTransactionInitiated } = useTransactionInitiated();
  const history = useHistory();
  const [selectedFund, setSelectedFund] = useState(defaultFund);
  const [submitted, setSubmitted] = useState(false);
  const fundType = useFundType();
  const refs = useRef([]);
  refs.current = [];
  const [isLimitLoading, setIsLimitLoading] = useState(false);
  const [limitAmountState, setLimitAmountState] = useState(limitAmount); // Because for Hybrid work, the limitAmount is getting 1, the transaction is not <= than $1 (Lower of wallet balance or Oper Max withdraw)
  const bankAccountLimitMet = fundingSourceList.current.length === 4; //fundType === 'netspend' ? fundingSources?.length === 3 : fundingSources?.length === 4;
  const [transactionAmount, setTransactionAmount] = useState(
    session.transactionAmount,
  );
  const [transactionAmt, setTransactionAmt] = useState(
    session.transactionAmount,
  );
  const { watch, control } = useForm({
    shouldUnregister: false,
    criteriaMode: "all",
    reValidateMode: "onBlur",
    defaultValues: {
      bankAccount: defaultFund.accountNumber,
    },
  });

  const bankAccount = watch("bankAccount");

  // New Code for Edit Amount
  const amountFieldRef = useRef(session.sessionStorageTransAmount);
  const domElements = [];
  const [selectedAmount, setSelectedAmount] = useState();
  const [btnEnabled, setBtnEnabled] = useState(false);
  const submitButtonText = withdraw ? "withdraw" : "deposit";
  const [displayLabel, setDisplayLabel] = useState(false);

  const isCashier = view === VIEW.cashier;
  var cashierCss = "";
  Object.values(session?.uiStyleFields).forEach((style) => {
    cashierCss += style;
  });

  useEffect(() => {
    if (!withdraw && isPlaid) {
      if (selectedFund) {
        let dailyLimit = Math.min(
          operatorDailyLimit,
          selectedFund.availableLimit,
        );
        setLimitAmountState(dailyLimit);
        window.sessionStorage.setItem(
          "selectedFundDetails",
          JSON.stringify(selectedFund),
        );
        if (session.toggles.isAmountEditable) {
          setTransactionAmount(transactionAmt);
          amountFieldRef.current = sessionStorageTransAmount;
        }
        setDisplayLabel(
          selectedFund.totalLimit > selectedFund?.vipoPreviousLimit,
        );
      } else {
        setLimitAmountState(operatorDailyLimit);
        setDisplayLabel(operatorDailyLimit > defaultFund?.vipoPreviousLimit);
      }
    } else {
      setLimitAmountState(limitAmount);
      setDisplayLabel(preferredCurrentLimit > preferredPreviousLimit);
    }
    setIsLimitLoading(false);
    setIsRepeat(true);
  }, [selectedFund, limitAmount, vipApi, withdraw]);

  useEffect(() => {
    if (bankAccount) {
      const account = fundingSources.current.find(
        (source) => source.accountNumber === bankAccount,
      );
      faro.api.pushEvent(
        `User selected account ${account.accountNumberDisplay} from dropdown list for transaction`,
      );
      setSelectedFund(account);
      if (!withdraw && isPlaid) {
        let dailyLimit = Math.min(
          operatorDailyLimit,
          selectedFund.availableLimit,
        );
        setLimitAmountState(dailyLimit);
        window.sessionStorage.setItem(
          "selectedFundDetails",
          JSON.stringify(account),
        );
        if (session.toggles.isAmountEditable) {
          setTransactionAmount(transactionAmt);
          amountFieldRef.current = sessionStorageTransAmount;
          setBtnEnabled(true);
        }
        setDisplayLabel(account.totalLimit > selectedFund.vipoPreviousLimit);
      }
    }
  }, [bankAccount]);

  function onLinkSelected(destination) {
    faro.api.pushEvent(
      `User clicked ${
        destination === "addAccount" ? "Add New" : "Manage"
      } Funding Source from dropdown list for transaction`,
    );
    if (isCashier) {
      vipApi
        .fetch("/api/patronsession/updateuiparams", "POST", {
          uiStartingPath: "/" + destination,
          uiStartingAction: FUND_SOURCE_TYPE.PLAID,
        })
        .then((response) => {
          setShowTerms(false);
          if (response.ok) {
            const platform = navigator.userAgent.toLowerCase();
            if (
              platform.includes("iphone") ||
              platform.includes("ipad") ||
              platform.includes("ipod")
            ) {
              window?.webkit?.messageHandlers?.NativeBridge?.postMessage(
                "opensdk",
              );
            } else if (platform.includes("android")) {
              window.postMessage("opensdk", "*");
            } else {
              window.postMessage("opensdk", "*");
            }
          } else {
            faro.api.pushError(
              new Error("Cashier Transfer Funds Update Error"),
              { context: response },
            );
            history.push({
              pathname: "/error",
              state: {
                isError: true,
                message:
                  "Unable to process your request at this time. For further assistance, please contact Pavilion Gaming Services customer support at 1-800-500-1973 with reference code E-99.",
                showButton: true,
                allowRetry: false,
              },
            });
          }
        })
        .catch((error) => {
          faro.api.pushError(
            new Error("Cashier Transfer Funds Network Error"),
            { context: error },
          );
          console.error(error);
          history.push({
            pathname: "/error",
          });
        });
    } else {
      if (destination === "addAccount") {
        history.push({
          pathname: "/addAccount",
          state: { notRedirect: true },
        });
      } else {
        history.push(destination);
      }
    }
  }

  function onFieldChange(fieldName, fieldValue, isValid) {
    if (session.toggles.isAmountEditable) {
      setTransactionAmt(fieldValue);
      setSessionStorageTransAmount(fieldValue);
    }
    setTransactionAmount(fieldValue);
    if (isValid) {
      setBtnEnabled(true);
    } else {
      setBtnEnabled(false);
    }
  }

  function formatTransferAmount(transferAmount) {
    if (!transferAmount) return transferAmount;
    if (transferAmount.toString().indexOf(".") < 0) return transferAmount;
    let parts = transferAmount.toString().split(".");
    switch (parts[1].length) {
      case 0:
        return transferAmount + "00";
      case 1:
        return transferAmount + "0";
      default:
        return transferAmount;
    }
  }

  async function onSuccess() {
    await fetchSession(true);
    window.sessionStorage.setItem("IsRedirectOrCancel", true);
    window.sessionStorage.setItem("plaidlaunch", false);
    const amount = formatTransferAmount(transactionAmount);
    const bankName = selectedFund.bankName;
    const oprName = session.operatorName;

    let customButtons = [];
    var redirectUrl = null;

    if (!session.toggles.showDashboardButtonAfterSuccess) {
      redirectUrl = {
        transactionSucceeded: true,
        returnUrl: session.returnUrl,
        transactionId: session.transactionId,
        transactionType: withdraw ? "withdraw" : "deposit",
      };
      customButtons.push({
        label:
          redirectUrl && !session.showTransactionConfirmation
            ? `You are being redirected to `
            : `CONTINUE TO ${oprName.toUpperCase()}`,
        returnToOperator: true,
        returnUrl: session.returnUrl,
        transactionSucceeded: true,
        transactionId: session.transactionId,
        transactionType: withdraw ? "withdraw" : "deposit",
      });
    } else {
      customButtons.push({
        label: `CONTINUE TO ${oprName.toUpperCase()}`,
        returnToOperator: true,
        returnUrl: session.returnUrl,
        transactionSucceeded: true,
        transactionId: session.transactionId,
        transactionType: withdraw ? "withdraw" : "deposit",
      });

      customButtons.push({
        label: `MANAGE ${productType.toUpperCase()}`,
        route: Routes.DASHBOARD,
        returnUrl: session.returnUrl,
        transactionSucceeded: true,
        transactionId: session.transactionId,
        transactionType: withdraw ? "withdraw" : "deposit",
      });
    }

    let location = {
      pathname: "/success",
      state: {
        isError: false,
        iconProps: { height: 200, width: 200 },
        title: session.toggles.depositApproval
          ? "Deposit In Progress"
          : "Deposit Successful",
        message: session.toggles.depositApproval
          ? `Your $${amount} deposit from your ${bankName} account to your ${oprName} account is currently in progress. The funds will be available in your ${oprName} account ${session?.uiDisplayFields?.transactionTimeframe}`
          : `Your $${amount} deposit was successful and your funds are now available in your ${oprName} account.`,
        customButtons,
        redirectUrl,
        amount,
        oprName,
        bankName,
      },
    };

    if (withdraw) {
      location.state.title = session.toggles.isInstantWithdrawals
        ? "Withdrawal Successful"
        : "Withdrawal In Progress";
      location.state.message = session.toggles.isInstantWithdrawals
        ? `You successfully transferred $${transactionAmount} ` +
          `from ${oprName} to your ${bankName} account.`
        : `The funds will be available shortly.
            Your ${amount} withdrawal from your ${oprName} account
            to your ${bankName} account is currently in progress.`;
      location.state.iconProps = session.toggles.isInstantWithdrawals
        ? { typeName: IconTypeNames.check }
        : { typeName: IconTypeNames.withdraw };
      location.state.amount = amount;
      location.state.oprName = oprName;
      location.state.bankName = bankName;
    }
    history.push(location);
  }

  function onError(response) {
    history.push({
      pathname: "/error",
      state: {
        isError: true,
        mitekRedirect: true,
        showButton: true,
        title: "Transaction Incomplete",
        message: response.patronErrorMessage ?? "Transaction Incomplete",
        allowRetry: false,
      },
    });
  }

  // Submit clicked
  async function onSubmit(event) {
    event.preventDefault();
    setSubmitted(true);
    faro.api.pushEvent(`User submitted a ${withdraw ? "withdraw" : "deposit"}`);
    const url = withdraw ? "/api/funds/withdraw" : "/api/funds/deposit";
    vipApi
      .fetch(url, "POST", {
        bankName: selectedFund.bankName,
        bankRoutingNumber: selectedFund.routingNumber,
        maskedBankAccountNumber: selectedFund.maskedAccountNumber,
        achOpenType: selectedFund.achOpenType,
        checkAmount: transactionAmount,
        ipAddress: window.sessionStorage.getItem("clientIpAddress"),
      })
      .then((response) => {
        setShowTerms(false);
        setTransactionInitiated(true);
        if (response.ok) {
          faro.api.pushEvent(
            `Successful ${withdraw ? "withdraw" : "deposit"} transaction`,
          );
          onSuccess();
        } else {
          faro.api.pushError(new Error("Transaction Incomplete Error"), {
            context: response,
          });
          if (response.status === 500 || response.status === 504) {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          } else if (response.status === 401) {
            console.error(response.statusText);
            history.push({
              pathname: "/expired",
            });
          } else if (response.status === 400) {
            response.json().then((data) => {
              history.push({
                pathname: "/error",
                state: {
                  isError: true,
                  title: "Transaction Incomplete",
                  message:
                    data.patronErrorMessage ??
                    "Unable to process your request at this time. For further assistance, please contact Pavilion Gaming Services customer support at 1-800-500-1973 with reference code E-99.",
                  showButton: true,
                  allowRetry: false,
                },
              });
            });
          } else {
            response.json().then((data) => {
              console.error(data);
              faro.api.pushError(new Error("Transaction Error"), {
                context: data,
              });
              onError(data);
            });
          }
        }
      })
      .catch((error) => {
        console.error(error);
        faro.api.pushError(new Error("Transaction Incomplete Network Error"), {
          context: error,
        });
        history.push({
          pathname: "/error",
        });
      });
  }

  // JSX Fragments

  const selectAmount = (amount) => {
    if (amount !== selectedAmount) {
      if (session.toggles.isAmountEditable) {
        setTransactionAmt(amount);
        setSessionStorageTransAmount(amount);
      }
      setTransactionAmount(amount);
      setSelectedAmount(amount);
      amountFieldRef.current = amount;
      setBtnEnabled(true);
    } else {
      if (session.toggles.isAmountEditable) {
        setTransactionAmt("");
        setSessionStorageTransAmount("");
      }
      setTransactionAmount("");
      setSelectedAmount("");
      amountFieldRef.current = "";
      setBtnEnabled(true);
    }
  };

  const denominations = [25, 50, 100, 200, 500];
  let count = 0;
  const amounts = denominations.map((amount, index) => {
    const disabled = amount > limitAmountState;
    if (amount <= limitAmountState) count++;
    return (
      <Box
        key={amount}
        sx={{
          display: disabled ? "none" : "flex",
          alignItems: "center",
          maxWidth: "100%",
          minWidth: "40%",
          flex: "1",
        }}
      >
        {
          <Button
            ref={(node) => domElements.push(node)}
            disabled={disabled}
            buttonType={"secondary"}
            fullWidth
            onClick={() => {
              selectAmount(amount);
            }}
            backgroundcolor={
              amount === selectedAmount
                ? "var(--color-surface-button-secondary-pressed)"
                : undefined
            }
          >
            ${amount}
          </Button>
        }
      </Box>
    );
  });

  const amountButtons = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: "12px",
        flexWrap: "wrap",
      }}
    >
      {amounts}
    </Box>
  );

  const proceed =
    transactionAmount &&
    transactionAmount >= minAmount &&
    transactionAmount <= limitAmountState &&
    selectedFund;
  const showAmountExceeded =
    !session.toggles.isAmountEditable &&
    !(
      transactionAmount &&
      transactionAmount >= minAmount &&
      transactionAmount <= limitAmountState &&
      selectedFund
    );

  const submitButton = (
    <Button
      buttonType={submitted ? "secondary" : "primary"}
      fullWidth
      padding={
        isCashier ? "var(--spacing-md) var(--spacing-lg)" : "var(--spacing-lg)"
      }
      onClick={onSubmit}
      disabled={
        session.toggles.isAmountEditable ? !(proceed && btnEnabled) : !proceed
      }
      startIcon={
        submitted && (
          <CircularProgress
            sx={{ color: "var(--color-surface-button-primary-enabled)" }}
            size="1.5rem"
          />
        )
      }
    >
      {submitted ? "Processing" : withdraw ? "Withdraw" : "Deposit"}
    </Button>
  );

  let limitSection;

  if (isLimitLoading) {
    limitSection = (
      <div className="limit text-center">CHECKING CARD BALANCE...</div>
    );
  } else {
    if (withdraw) {
      limitSection = (
        <AvailableFundsBase
          widthdraw
          availableFunds={transactionAmount}
          headerText={`Amount selected on ${session.operatorName}`}
        />
      );
    } else if (fundType === "netspend") {
      // netspend deposit
      limitSection = (
        <div className="limit text-center">
          ${limitAmountState?.toLocaleString()} CARD BALANCE
        </div>
      );
    } else {
      // ach deposit
      limitSection = (
        <AvailableFundsBase
          availableFunds={transactionAmount}
          headerText={`Amount selected on ${session.operatorName}`}
          deposit={true}
        />
      );
    }
  }

  const bankOption = bankAccountLimitMet ? (
    <Button
      onClick={() => {
        onLinkSelected("myAccounts");
      }}
      fullWidth
      borderRadius="8px"
      startIcon={<img src={settings} alt="Manage Bank" />}
      padding="var(--spacing-sm) var(--spacing-md)"
    >
      Manage Funding Sources
    </Button>
  ) : (
    <Button
      onClick={() => {
        onLinkSelected("addAccount");
      }}
      fullWidth
      borderRadius="8px"
      padding="var(--spacing-sm) var(--spacing-md)"
      startIcon={<AddCircleIcon />}
    >
      Add New Funding Source
    </Button>
  );
  const manageFundingOption = !bankAccountLimitMet && (
    <Button
      onClick={() => {
        onLinkSelected("myAccounts");
      }}
      fullWidth
      borderRadius="8px"
      startIcon={<img src={settings} alt="Manage Bank" />}
      padding="var(--spacing-sm) var(--spacing-md)"
    >
      Manage Funding Sources
    </Button>
  );

  const MINIMUM_AMOUNT = {
    errorMessage:
      limitAmountState < minAmount
        ? `Your deposit limit is less than the minimum ${session.operatorName.toUpperCase()} deposit amount of $${minAmount}.`
        : `Amount must be equal to or greater than $${minAmount}.`,
    action: (amount) => amount >= minAmount,
  };

  const MAXIMUM_AMOUNT = {
    errorMessage:
      limitAmountState < minAmount
        ? `Your deposit limit is less than the minimum ${session.operatorName.toUpperCase()} deposit amount of $${minAmount}.`
        : `Amount must be equal to or less than $${limitAmountState}.`,
    action: (amount) => amount <= limitAmountState,
  };

  const DOLLAR_CENTS = {
    errorMessage: "Must be a valid amount.",
    regex: /^[0-9]+(\.[0-9]?[0-9]?)?$/,
  };
  const unavailableIcon = (
    <img
      height="20"
      alt="Insufficient Funds in Account"
      className="menu-icon"
      src={errorIcon}
    />
  );

  return (
    <>
      {isCashier ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            className="transfer-funds"
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <style>{cashierCss}</style>
            <div className="cashier-background">
              {!withdraw && (
                <Box
                  sx={{
                    color: "var(--color-text-primary)",
                    fontFamily: "var(--body-font-family)",
                    fontSize: "var(--body-size)",
                    fontWeight: 700,
                    padding: "0 0 var(--spacing-md) 0",
                    textAlign: { xs: "left", md: "center" },
                  }}
                >
                  Your available deposit limit is $
                  {AddTrailingZeros(limitAmountState?.toLocaleString())}
                </Box>
              )}

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--spacing-md)",
                    marginBottom: "var(--spacing-md)",
                  }}
                >
                  {session.toggles.isAmountEditable &&
                    !withdraw &&
                    amountButtons}

                  {session.toggles.isAmountEditable && (
                    <FormField
                      prepend="$"
                      fieldName="amount"
                      label="AMOUNT"
                      maxLength="20"
                      startValue={
                        !isNaN(sessionStorageTransAmount)
                          ? formatTransferAmount(sessionStorageTransAmount)
                          : undefined
                      }
                      required={true}
                      validators={[
                        DOLLAR_CENTS,
                        MINIMUM_AMOUNT,
                        MAXIMUM_AMOUNT,
                      ]}
                      onValueChanged={onFieldChange}
                      autocomplete="off"
                      ref={amountFieldRef}
                      append={`min $${limitAmountState > 0 ? minAmount : 0}<br />max $${limitAmountState}`}
                    />
                  )}
                </Box>

                <Box sx={{ marginBottom: "32px" }}>
                  <Controller
                    rules={{ required: true }}
                    name="bankAccount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        label="Bank Account"
                        fullWidth
                        fieldtype="select"
                        selectMenuMargin="var(--spacing-sm) 0 0 0"
                        selectMenuPadding="var(--spacing-md) var(--spacing-lg)"
                        selectMenuBorderRadius="8px"
                        selectMenuBoxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.20)"
                        selectMenuItemBorderRadius="8px"
                        selectMenuItemHoverBackgroundColor="var(--color-surface-button-tertiary-hover)"
                        selectMenuItemActiveBackgroundColor="var(--color-surface-button-tertiary-hover)"
                        data-testid="bank-account-dropdown"
                      >
                        {fundingSources.current?.map((bankAccount, index) => {
                          const accountAvailable =
                            (!isVIPPreferred &&
                              !withdraw &&
                              transactionAmt < bankAccount.availableLimit) ||
                            isVIPPreferred ||
                            withdraw;
                          return (
                            <MenuItem
                              disabled={!accountAvailable}
                              key={index}
                              value={bankAccount.accountNumber}
                              sx={{
                                margin: "0 0 4px 0",
                                borderRadius: "8px",
                                padding: "var(--spacing-sm) var(--spacing-md)",
                              }}
                            >
                              {!accountAvailable && unavailableIcon}
                              {bankAccount.bankName
                                ?.toUpperCase()
                                .substring(0, 24) +
                                " x" +
                                bankAccount.accountNumberDisplay}
                            </MenuItem>
                          );
                        })}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          {session.toggles.manageFundingSourcesInDropdown ===
                            true && bankOption}
                          {session.toggles.manageFundingSourcesInDropdown ===
                            true && manageFundingOption}
                        </Box>
                      </TextField>
                    )}
                  />
                </Box>

                {submitButton}
              </Box>
              <div className="cashier-limit-error">
                {showAmountExceeded && (
                  <span>
                    Transaction Amount entered exceeded Available limit.
                  </span>
                )}
              </div>
            </div>
          </Box>
        </Box>
      ) : (
        <div className="transfer-funds">
          <Box
            className="header"
            sx={{
              textAlign: { xs: "left", md: "center" },
              marginBottom: "12px",
            }}
          >
            Complete {withdraw ? "Withdrawal" : "Deposit"}
          </Box>
          {noWarranty && unVerifiedBank && isVIPPreferred && (
            <div className="text-center switch-message mt-3">
              Bank Verification was unsuccessful. We will continue using your
              existing manually entered bank account.
            </div>
          )}
          <Box sx={{ marginBottom: withdraw ? "12px" : undefined }}>
            {!session.toggles.isAmountEditable && selectedFund && limitSection}
          </Box>
          {!withdraw && (
            <div className="limit">
              {session.toggles.isAmountEditable ? (
                <Box
                  sx={{
                    color: "var(--color-text-primary)",
                    fontFamily: "var(--body-font-family)",
                    fontSize: "var(--body-size)",
                    fontWeight: 700,
                    padding: "var(--spacing-md) 0",
                    textAlign: { xs: "left", md: "center" },
                  }}
                >
                  ${AddTrailingZeros(limitAmountState?.toLocaleString())}{" "}
                  Deposit Limit
                </Box>
              ) : (
                <Box
                  sx={{
                    color: "var(--color-text-alternate)",
                    fontFamily: "var(--caption-font-family)",
                    fontSize: "14px",
                    fontWeight: 400,
                    padding: "var(--spacing-md) 0",
                    textAlign: { xs: "left", md: "center" },
                  }}
                >
                  ${AddTrailingZeros(limitAmountState?.toLocaleString())}{" "}
                  Deposit Limit
                </Box>
              )}
              {displayLabel && (
                <div>
                  <label className="limit-increase-label">
                    &#9432; Your deposit limit has increased!
                  </label>
                </div>
              )}
            </div>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--spacing-md)",
            }}
          >
            {session.toggles.isAmountEditable && !withdraw && amountButtons}
            {session.toggles.isAmountEditable && (
              <FormField
                prepend={
                  <AttachMoneyRoundedIcon
                    style={{ color: "#2F302F", fontSize: "24px" }}
                  />
                }
                fieldName="amount"
                label="AMOUNT"
                maxLength="20"
                startValue={
                  !isNaN(sessionStorageTransAmount)
                    ? formatTransferAmount(sessionStorageTransAmount)
                    : undefined
                }
                required={true}
                inputPadding="15px 10px 0px 10px"
                labelMargin="0 0 10px 15px"
                validators={[DOLLAR_CENTS, MINIMUM_AMOUNT, MAXIMUM_AMOUNT]}
                onValueChanged={onFieldChange}
                autocomplete="off"
                ref={amountFieldRef}
                append={`min $${limitAmountState > 0 ? minAmount : 0}<br />max $${limitAmountState}`}
              />
            )}
            <Controller
              rules={{ required: true }}
              name="bankAccount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="Bank Account"
                  fullWidth
                  fieldtype="select"
                  selectMenuMargin="var(--spacing-sm) 0 0 0"
                  selectMenuPadding="var(--spacing-md) var(--spacing-lg)"
                  selectMenuBorderRadius="8px"
                  selectMenuBoxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.20)"
                  selectMenuItemBorderRadius="8px"
                  selectMenuItemHoverBackgroundColor="var(--color-surface-button-tertiary-hover)"
                  selectMenuItemActiveBackgroundColor="var(--color-surface-button-tertiary-hover)"
                  InputLabelProps={{
                    required: false, // hides the asterisk
                  }}
                >
                  {fundingSources.current?.map((bankAccount, index) => {
                    const accountAvailable =
                      (!isVIPPreferred &&
                        !withdraw &&
                        transactionAmt < bankAccount.availableLimit) ||
                      isVIPPreferred ||
                      withdraw;
                    return (
                      <MenuItem
                        disabled={!accountAvailable}
                        key={index}
                        value={bankAccount.accountNumber}
                        sx={{
                          margin: "0 0 4px 0",
                          borderRadius: "8px",
                          padding: "var(--spacing-sm) var(--spacing-md)",
                        }}
                      >
                        {!accountAvailable && unavailableIcon}
                        {bankAccount.bankName?.toUpperCase().substring(0, 24) +
                          " x" +
                          bankAccount.accountNumberDisplay}
                      </MenuItem>
                    );
                  })}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    {session.toggles.manageFundingSourcesInDropdown === true &&
                      bankOption}
                    {session.toggles.manageFundingSourcesInDropdown === true &&
                      manageFundingOption}
                  </Box>
                </TextField>
              )}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "32px 0 0 0",
                gap: "10px",
              }}
            >
              {submitButton}
              <CancelButton
                btnText={withdraw ? "Withdrawal" : "Deposit"}
                stage={withdraw ? "Withdrawal" : "Deposit"}
              ></CancelButton>
            </Box>
            <Box className="text-center center-block">
              {showAmountExceeded && (
                <span className="available-limit-error">
                  Transaction Amount entered exceeded Available limit.
                </span>
              )}
            </Box>
          </Box>
        </div>
      )}
    </>
  );
};

export default TransferFunds;
